import { t } from '@sm/intl';
import { Feature, FeatureSetMap } from '~shared/types/pricing/pricingsvc';
import { FeatureMapItem } from '~pricing/transformers/types';

import detailsFeatureRules from '~pricing/transformers/details/rules';

import CELL_COPY from '~pricing/lib/PackageFeatures/details/cellCopy';

import {
  DetailsFeatureCategoryGroups,
  DetailsFeatureSets,
  DetailsFeatureValueType,
  DetailsFeatureRow,
  DetailsFeatureRowFeature,
  FeaturesCategoryGroup,
  DetailsFeatureRulesMap,
  DetailsPackageData,
} from './types';

import { getFormattedAmountValue } from './helpers';
import combinedFeatures from './combinedFeatures';
import { SETTING_UNLIMITED } from '../summary/rules/featureRules';
import { PackageDataSet } from '~shared/types/pricing/package';

/**
 * Determines the value to display for that feature based on its featureRule given the following preconditions
 * @param packageName
 * @param feature
 * @param locale
 * @returns
 */
export function getFeatureValue(
  packageName: string,
  feature: Feature,
  locale: string,
  experience: string,
  canonicalPath: string
): DetailsFeatureValueType {
  const featureRulesObj = canonicalPath
    ? detailsFeatureRules[canonicalPath as keyof DetailsFeatureRulesMap]
    : detailsFeatureRules.teams;
  const rules = experience && featureRulesObj[experience] ? featureRulesObj[experience] : featureRulesObj.default;

  if (feature?.amount) {
    return rules[feature?.name]
      ? rules[feature.name](packageName, feature, { locale })
      : getFormattedAmountValue(feature, { locale });
  }

  if (feature?.setting === SETTING_UNLIMITED) {
    return t(CELL_COPY.UNLIMITED);
  }

  if (feature?.setting) {
    return rules[feature?.name] ? rules[feature.name](packageName, feature) : feature?.setting;
  }

  return rules[feature?.name] ? rules[feature.name](packageName) : feature?.isEnabled;
}

/**
 * Determines if the current feature being looped over is part of a combined list,
 * meaning only show one line item for a set of given features, otherwise just show the feature as its own line item
 * @param packageName
 * @param packageFeatures
 * @param feature
 * @param locale
 * @returns
 */
export function determineCombinedFeature(
  packageName: string,
  packageFeatures: Feature[],
  feature: string,
  locale: string,
  experience: string,
  canonicalPath: string
): DetailsFeatureValueType {
  const combinedFeature: FeatureMapItem =
    combinedFeatures.find(combined => combined.features.includes(feature)) ?? null;
  if (combinedFeature) {
    const features = combinedFeature?.features?.reduce<FeatureSetMap>((acc, key) => {
      const foundFeature = packageFeatures.find(pkg => pkg.name === key);

      if (foundFeature) {
        acc[key] = foundFeature;
      }
      return acc;
    }, {});

    const featureRulesObj = detailsFeatureRules[canonicalPath as keyof DetailsFeatureRulesMap];
    const rules = experience && featureRulesObj[experience] ? featureRulesObj[experience] : featureRulesObj.default;

    return features ? rules[combinedFeature.featureItemKey](packageName, features, { locale }) : null;
  }
  return null;
}

/**
   * Returns an array with the following structure of features grouped by category
   * [
    {
      name: 'Team Collaboration',
      features: [
        {
          name: 'team_themes',
          summary: 'Summary description text',
          tooltip: 'Tooltip description text,
          tooltipHeading: 'Tooltip heading text',
          packages: {
            ADVANTAGE_ANNUAL: value,
            ADVANTAGE_MONTHLY: value,
            BASIC: value,
            ENT_PLATINUM: value,
            ...
          }
        },
        ...
      ],
    },
    {
      name: 'Survey Capabilities',
      features: [
        {
          name: 'survey_create_limit',
          summary: 'Number of surveys',
          tooltip: 'Tooltip description text,
          tooltipHeading: 'Tooltip heading text',
          packages: {
            ADVANTAGE_ANNUAL: value,
            ADVANTAGE_MONTHLY: value,
            BASIC: value,
            ENT_PLATINUM: value,
            ...
          }
        },
        ...
      ],
    },
  ];
  */

/**
 * Transforms detail pages features
 * This is the main function that loops over all category groupings and which features they contain
 * @param displayedPackages
 * @param groupings
 * @param detailsPackageData
 * @returns
 */
export default function getFeatureCategories(
  displayedPackages: PackageDataSet,
  groupings: DetailsFeatureCategoryGroups,
  detailsPackageData: DetailsPackageData
): DetailsFeatureSets {
  if (displayedPackages) {
    return groupings.map(({ category, features }: FeaturesCategoryGroup): DetailsFeatureRow => {
      return {
        name: category,
        features: features?.map((feature: string): DetailsFeatureRowFeature => {
          const { name, summary, tooltip, tooltipHeading, ...rest } = detailsPackageData[feature];
          return {
            name,
            summary,
            tooltip,
            tooltipHeading,
            packages: rest,
          };
        }),
      };
    });
  }

  return [];
}
